import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faBellSlash,
  faBriefcase,
  faBuilding,
  faChevronRight,
  faGlobe,
  faHeadset,
  faHome,
  faIdCard,
  faLaptop,
  faMousePointer,
  faPhone,
  faPlane,
  faUsers,
  faUserTie,
} from '@fortawesome/free-solid-svg-icons';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const rows = [
  { name: 'Ashley Davis', role: 'VP Sales - Acme Corp', phone: '+1 (415) 555 0142' },
  { name: 'Lina Park', role: 'Founder - Northwind', phone: '+44 20 7946 0123' },
  { name: 'Diego Alvarez', role: 'Head Recruiter - Helio', phone: '+34 911 23 45 67' },
  { name: 'Maya Tanaka', role: 'Realtor - Bay & Cove', phone: '+81 3 1234 5678' },
];

const features = [
  {
    icon: faPhone,
    title: 'One-tap calling',
    desc: 'Call any number you see on a web page without dialing or switching devices.',
  },
  {
    icon: faGlobe,
    title: 'Your own global number',
    desc: 'Buy a virtual number in the city or country you want and keep it on a monthly plan.',
  },
  {
    icon: faHeadset,
    title: 'Call, text, and receive',
    desc: 'Make and take calls and SMS from one number, on extension or app.',
  },
  {
    icon: faIdCard,
    title: 'Caller ID control',
    desc: 'Choose which number shows on the other end, or set it to always ask.',
  },
  {
    icon: faBellSlash,
    title: 'Do Not Disturb',
    desc: 'Flip one switch to pause incoming calls when you need focus time.',
  },
  {
    icon: faUserTie,
    title: 'Contacts, history, favorites',
    desc: 'Address book, call history, and favorite contacts all in one dashboard.',
  },
];

const useCases = [
  {
    icon: faPlane,
    tag: 'Travelers & remote workers',
    desc: 'Land in a new country and get a local number in minutes. Call and text like a local and skip expensive roaming.',
  },
  {
    icon: faBriefcase,
    tag: 'Sales & outreach teams',
    desc: 'Call prospects straight from LinkedIn, directories, and lead lists without copy-pasting into a dialer.',
  },
  {
    icon: faHeadset,
    tag: 'Customer support & success',
    desc: 'Return customer calls in one tap, with call history and favorites on hand.',
  },
  {
    icon: faUsers,
    tag: 'Recruiters & agencies',
    desc: 'Call candidates the moment you spot a number, with a dedicated business line.',
  },
  {
    icon: faHome,
    tag: 'Real estate, auto, field sales',
    desc: 'Call a listing, dealer, or supplier directly from any page, instantly, from your laptop.',
  },
  {
    icon: faLaptop,
    tag: 'Freelancers & solo founders',
    desc: 'Get a professional business number without buying a second phone or SIM.',
  },
  {
    icon: faBuilding,
    tag: 'Global businesses',
    desc: 'Hold local numbers in several countries so customers see a familiar caller ID.',
  },
  {
    icon: faGlobe,
    tag: 'Multiple lines',
    desc: 'Manage several virtual numbers from one app and switch between them easily.',
  },
];

const reasons = [
  ['01', 'No hardware, no second SIM.', 'Calling runs over the internet from your browser or app.'],
  ['02', 'Be local anywhere.', 'Pick numbers by country and area code to build trust and lift answer rates.'],
  ['03', 'Faster than dialing.', 'Every on-screen number becomes a one-tap call.'],
  ['04', 'Stay in control.', 'Caller ID options, Do Not Disturb, and full call history.'],
  ['05', 'Flexible.', 'Keep a number as long as you need it; if you stop subscribing, the number is released.'],
];

const signupUrl = 'https://app-phone.contexttocall.com/extension/sign-in';
const demoUrl = 'https://chromewebstore.google.com/detail/c2c-softphone/fffggfljjanagmgbckhobjeepebcphpi';

const SectionLabel = ({ children, number }) => (
  <div className="softphone-section-label">
    <span>{number}</span>
    <i />
    {children}
  </div>
);

const SoftphoneMockup = () => (
  <div className="softphone-mockup" data-testid="hero-product-mockup">
    <div className="softphone-mockup-glow" aria-hidden="true" />
    <div className="softphone-browser">
      <div className="softphone-browser-top">
        <div className="softphone-window-dots">
          <span />
          <span />
          <span />
        </div>
        <div className="softphone-url">
          <span />
          https://app-phone.contexttocall.com/extension
        </div>
        <small>LIVE DEMO</small>
      </div>

      <div className="softphone-browser-body">
        <div className="softphone-browser-meta">
          <span>Leads - 4</span>
          <span>softphone.detect()</span>
        </div>
        {rows.map((row, index) => (
          <LeadRow key={row.phone} row={row} isTarget={index === 1} />
        ))}
        <CallingBadge />
      </div>
    </div>

    <motion.div
      aria-hidden="true"
      className="softphone-cursor"
      initial={{ x: 40, y: 230, opacity: 0 }}
      animate={{
        opacity: [0, 1, 1, 1, 1, 0],
        x: [40, 220, 310, 310, 310, 310],
        y: [230, 210, 185, 185, 185, 185],
      }}
      transition={{ duration: 5, times: [0, 0.15, 0.45, 0.6, 0.85, 1], repeat: Infinity, repeatDelay: 1.2, ease: 'easeInOut' }}
    >
      <FontAwesomeIcon icon={faMousePointer} />
    </motion.div>

    <div className="softphone-active-badge">
      <FontAwesomeIcon icon={faPhone} />
      Softphone active
    </div>
  </div>
);

const LeadRow = ({ row, isTarget }) => (
  <div className={`softphone-lead-row ${isTarget ? 'target' : ''}`}>
    <div>
      <strong>{row.name}</strong>
      <span>{row.role}</span>
    </div>
    <div className="softphone-lead-phone">
      <span>{row.phone}</span>
      {isTarget && (
        <motion.button
          type="button"
          aria-label="Call with Softphone"
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: [0, 0, 1, 1, 1, 0], scale: [0.6, 0.6, 1, 1, 0.9, 0.6] }}
          transition={{ duration: 5, times: [0, 0.3, 0.45, 0.7, 0.85, 1], repeat: Infinity, repeatDelay: 1.2 }}
        >
          <FontAwesomeIcon icon={faPhone} />
        </motion.button>
      )}
    </div>
  </div>
);

const CallingBadge = () => (
  <motion.div
    className="softphone-calling-badge"
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: [0, 0, 0, 1, 1, 0], y: [8, 8, 8, 0, 0, 0] }}
    transition={{ duration: 5, times: [0, 0.3, 0.55, 0.65, 0.85, 1], repeat: Infinity, repeatDelay: 1.2 }}
  >
    <div>
      <span className="softphone-pulse" />
      <div>
        <strong>Calling - Softphone</strong>
        <small>+44 20 7946 0123</small>
      </div>
    </div>
    <span>00:02</span>
  </motion.div>
);

export default function SoftphonePage() {
  return (
    <main className="softphone-page" data-testid="softphone-landing">
      <section className="softphone-hero">
        <div className="section-container softphone-hero-grid">
          <div className="softphone-hero-copy">
            <Badge className="product-badge softphone">
              <FontAwesomeIcon icon={faPhone} />
              C2C Softphone
            </Badge>
            <h1>
              Tap to call <span>any number on your screen.</span>
            </h1>
            <p>
              Softphone puts a call button next to every phone number you see online, and gives you your own number you can use from anywhere in the world.
              No dialing. No second phone. No roaming.
            </p>
            <div className="hero-ctas">
              <Button size="lg" className="btn-softphone" asChild>
                <a href={signupUrl} target="_blank" rel="noopener noreferrer">
                  Get your number <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
                </a>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href={demoUrl} target="_blank" rel="noopener noreferrer">
                  Chrome Extension
                </a>
              </Button>
            </div>
            <div className="softphone-stats">
              <div><strong>1-tap</strong><span>Calling from any page</span></div>
              <div><strong>30+</strong><span>Country area codes</span></div>
              <div><strong>0</strong><span>Extra SIMs needed</span></div>
            </div>
          </div>
          <SoftphoneMockup />
        </div>
      </section>

      <section className="softphone-band">
        <div className="section-container">
          <SectionLabel number="/01">What is Softphone</SectionLabel>
          <h2>
            Softphone is your phone, built into your <strong>browser</strong> and companion app.
            See a number on any website, tap the Softphone icon beside it, and you are <strong>instantly connected</strong>.
          </h2>
        </div>
      </section>

      <section className="softphone-band softphone-steps-section">
        <div className="section-container">
          <SectionLabel number="/02">How it works</SectionLabel>
          <div className="softphone-steps">
            {[
              ['01', 'Sign up and pick your number', 'Choose a virtual number in the location of your choice.'],
              ['02', 'Install the extension or app', 'Add Softphone to Chrome or Edge, or download the companion app.'],
              ['03', 'Tap to call', 'Every number on screen gets a call icon. One tap connects you.'],
            ].map(([number, title, desc], index) => (
              <div className="softphone-step" key={number}>
                <span>{number}</span>
                <h3>{title}</h3>
                <p>{desc}</p>
                <small><FontAwesomeIcon icon={faChevronRight} /> {index === 2 ? 'Done' : 'Next'}</small>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="softphone-band">
        <div className="section-container">
          <SectionLabel number="/03">Key features</SectionLabel>
          <div className="softphone-section-heading">
            <h2>Everything a phone should do. <strong>Nothing it should not.</strong></h2>
            <p>Designed for power users who pick up the phone all day without picking up a phone.</p>
          </div>
          <div className="softphone-feature-grid">
            {features.map((feature, index) => (
              <article key={feature.title} className="softphone-feature-card">
                <FontAwesomeIcon icon={feature.icon} />
                <h3>{feature.title}</h3>
                <p>{feature.desc}</p>
                <span>{String(index + 1).padStart(2, '0')}</span>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="softphone-band">
        <div className="section-container">
          <SectionLabel number="/04">Who it is for</SectionLabel>
          <div className="softphone-section-heading">
            <h2>Built for everyone who picks up the phone for a living.</h2>
          </div>
          <div className="softphone-usecase-grid">
            {useCases.map((item) => (
              <article key={item.tag} className="softphone-usecase-card">
                <FontAwesomeIcon icon={item.icon} />
                <h3>{item.tag}</h3>
                <p>{item.desc}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="softphone-band">
        <div className="section-container softphone-reasons">
          <div>
            <SectionLabel number="/05">Why Softphone</SectionLabel>
            <h2>Five reasons it just clicks.</h2>
            <p>We rebuilt the phone around the browser because that is where your work already lives.</p>
          </div>
          <div className="softphone-reason-list">
            {reasons.map(([number, title, desc]) => (
              <div key={number} className="softphone-reason">
                <span>{number}</span>
                <h3>{title}</h3>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="softphone-final-cta">
        <div className="section-container">
          <div>
            <SectionLabel number="/06">Final step</SectionLabel>
            <h2>Get a number. Start calling in minutes.</h2>
            <p>Add the extension to Chrome or Edge, or download the app, and tap to call.</p>
          </div>
          <div className="softphone-final-actions">
            <Button size="lg" asChild>
              <a href={signupUrl} target="_blank" rel="noopener noreferrer">
                Get your Softphone number
              </a>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/#products">Back to Products</Link>
            </Button>
          </div>
        </div>
      </section>
    </main>
  );
}
